import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { 
  ArrowLeft, 
  Save,
  Loader2,
  FileText,
  Eye
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminHomepageEditor() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState({
    hero_title_line1: "",
    hero_title_line2: "",
    hero_subtitle: "",
    ai_tools_title: "",
    ai_tools_subtitle: "",
    aspiring_title: "",
    aspiring_subtitle: ""
  });

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/homepage-content`);
      if (res.ok) {
        const data = await res.json();
        setContent(data);
      }
    } catch (err) {
      console.error("Failed to fetch homepage content:", err);
      toast.error("Failed to load content");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/homepage-content`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(content)
      });
      
      if (res.ok) {
        toast.success("Homepage content saved successfully");
      } else {
        throw new Error("Failed to save");
      }
    } catch (err) {
      console.error("Failed to save homepage content:", err);
      toast.error("Failed to save content");
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setContent(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#121318] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#121318]">
      {/* Navigation */}
      <nav className="border-b border-slate-700 bg-slate-900/50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button 
            onClick={() => navigate('/admin-area')}
            variant="ghost"
            className="text-slate-400 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Admin
          </Button>
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-teal-400" />
            <span className="text-teal-400 font-medium">Homepage Editor</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => window.open('/', '_blank')}
              variant="outline"
              size="sm"
              className="border-slate-600 text-slate-300"
            >
              <Eye className="w-4 h-4 mr-2" />
              Preview
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-teal-500 hover:bg-teal-600"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Changes
            </Button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        {/* Hero Section */}
        <Card className="bg-slate-900/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white">Hero Section</CardTitle>
            <CardDescription className="text-slate-400">
              The main headline and subtitle at the top of the homepage
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Title Line 1</Label>
                <Input
                  value={content.hero_title_line1}
                  onChange={(e) => handleChange("hero_title_line1", e.target.value)}
                  className="bg-slate-800 border-slate-600 text-white"
                  placeholder="The Operating System"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Title Line 2 (highlighted)</Label>
                <Input
                  value={content.hero_title_line2}
                  onChange={(e) => handleChange("hero_title_line2", e.target.value)}
                  className="bg-slate-800 border-slate-600 text-white"
                  placeholder="for Therapeutics Founders"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Subtitle</Label>
              <Textarea
                value={content.hero_subtitle}
                onChange={(e) => handleChange("hero_subtitle", e.target.value)}
                className="bg-slate-800 border-slate-600 text-white min-h-[100px]"
                placeholder="Built exclusively for UK therapeutic startups..."
              />
            </div>
          </CardContent>
        </Card>

        {/* AI Tools Section */}
        <Card className="bg-slate-900/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white">AI Tools Section</CardTitle>
            <CardDescription className="text-slate-400">
              The section showcasing AI-Investor Office Hours and Event Intelligence
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Section Title</Label>
              <Input
                value={content.ai_tools_title}
                onChange={(e) => handleChange("ai_tools_title", e.target.value)}
                className="bg-slate-800 border-slate-600 text-white"
                placeholder="AI-Powered Preparation Tools"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Section Subtitle</Label>
              <Input
                value={content.ai_tools_subtitle}
                onChange={(e) => handleChange("ai_tools_subtitle", e.target.value)}
                className="bg-slate-800 border-slate-600 text-white"
                placeholder="Practice and refine before your most important meetings"
              />
            </div>
          </CardContent>
        </Card>

        {/* Aspiring Founders Section */}
        <Card className="bg-slate-900/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white">Aspiring Founders Section</CardTitle>
            <CardDescription className="text-slate-400">
              The bottom section linking to Operon Start
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Section Title</Label>
              <Input
                value={content.aspiring_title}
                onChange={(e) => handleChange("aspiring_title", e.target.value)}
                className="bg-slate-800 border-slate-600 text-white"
                placeholder="Are you an aspiring founder?"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Section Description</Label>
              <Textarea
                value={content.aspiring_subtitle}
                onChange={(e) => handleChange("aspiring_subtitle", e.target.value)}
                className="bg-slate-800 border-slate-600 text-white min-h-[100px]"
                placeholder="Access Operon Start to understand how to make the jump..."
              />
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
